<script setup lang="ts">
/**
 * RefChip (IR-116 spec §7, generalised by MYAC-1172) — THE reference chip.
 *
 * One markup AND one behavioural contract for every reference the app shows:
 * a requirement, a clause, an obligation/schedule, a finding, a document, a
 * task, and the `+n` overflow that stands for the ones that did not fit.
 *
 * ## Why this component owns the behaviour too
 *
 * It was extracted after six copy-pastes (inventory #40) and the LOOK
 * converged — the BEHAVIOUR did not. The census of 2026-08-27 found the same
 * chip opening a dialog on one surface, navigating away on another, and doing
 * nothing on a third; and because the chip was ALWAYS styled clickable, three
 * surfaces with nothing to open cloned its markup into static spans rather
 * than use it (`RemediationRow`, `RemediationDetailModal`, `ConsistencyFinding
 * Row`). The copy-paste the extraction killed was growing back.
 *
 * So the contract lives here, in three states, and a call site picks one:
 *
 * | State | How | What the chip is |
 * | --- | --- | --- |
 * | **Interactive** (default) | `@click` | `role=button`, hover accent, stops the click. **Opens a dialog IN PLACE** — never navigates away (Sy, 2026-08-21). |
 * | **Static** | `:interactive="false"` | No cursor, no role, no focus stop. Clicks pass THROUGH to the surrounding row, which is usually the real control. |
 * | **Degraded link** | `href` | A real `<a target="_blank">`. The documented fallback for a reference carrying no identity to resolve — see below. |
 *
 * Plus `gone`: the reference existed and no longer resolves. Visible and
 * disabled with its reason, never hidden (front CLAUDE.md). It keeps
 * `role="button"` alongside `aria-disabled` (review, Yannholo): a bare `span`
 * has the implicit `generic` role, which does not support `aria-disabled`, so
 * the state was there for anyone reading the DOM and absent for anyone
 * listening to it — a disabled control has to SAY it is one. Deliberately not
 * focusable: `tabindex` marks what can still be operated, and this cannot.
 *
 * ## The degraded `href` mode
 *
 * A task accepted before anchors carried a `requirement_uid` (pre-MYAC-1093)
 * names a requirement it cannot identify; guessing from a code that is not
 * unique across referentials would open the WRONG requirement silently (Sy:
 * "don't fix it, this is on dev"). Such a reference falls back to a deep link.
 * It lives in the component as a named degraded mode rather than as a second
 * kind of chip at one call site — which is what it was, and what made the same
 * gesture mean two things.
 *
 * ## Mono is for CODES
 *
 * `4.4 (changes)` and `+3` are identifiers and read as such; a finding title, a
 * schedule's object or a filename is prose and `font-mono` only made it harder
 * to read. The two surfaces disagreed on this (Complétude mono, the plan's
 * inline clones not) — one answer, decided per kind here.
 *
 * ## The `+n` overflow contract
 *
 * An overflow chip is a CONTROL when its surface can reveal what it hides
 * (Complétude's missing-documents rows expand in place), and a STATIC count
 * when it cannot — either because the hidden refs are not in the payload
 * (`anchors_more`) or because the surrounding row already opens something that
 * lists them all. What it is never again: styled clickable and bound to
 * nothing, which is how it shipped twice.
 */
import { computed } from 'vue'

export type RefChipKind =
  | 'requirement'
  | 'clause'
  | 'schedule'
  | 'finding'
  | 'document'
  | 'task'
  | 'overflow'

const props = withDefaults(
  defineProps<{
    kind: RefChipKind
    /** The chip's words. Omit when the default slot carries them instead. */
    label?: string
    title?: string
    /** False on a read-only surface: same chip, no click affordance. */
    interactive?: boolean
    /** Degraded mode — a reference with no identity to resolve. See above. */
    href?: string
    /**
     * `document` only: a document NOTION with no file behind it (IR-116 #16).
     *
     * It briefly dashed any kind, for an overflow chip standing for references
     * the projection never sent. That chip is gone — the overlay's footer says
     * it in words now — so the generalisation lost its only consumer and went
     * back with it. Vocabulary ahead of a consumer is what the `clause` review
     * flagged on this same component.
     */
    concept?: boolean
    /** The reference no longer resolves: disabled, with its reason in `title`. */
    gone?: boolean
    /**
     * Drop the kind's icon — for the one case that earns it: the surrounding
     * surface ALREADY marks this reference's kind, and saying it twice is
     * noise, not vocabulary (review, Yannholo on `TaskReferenceList`, whose
     * every row opens with its own `pi-check-square`).
     *
     * Not a styling escape hatch: the chip keeps its kind, its typography and
     * its whole click contract. Only the redundant glyph goes.
     */
    hideIcon?: boolean
  }>(),
  { interactive: true },
)

/**
 * The originating event rides along — MYAC-1172 QA round 3.
 *
 * A chip that opens an ANCHORED overlay needs the element that raised it
 * (`Popover.show(event, target)`), and `event.currentTarget` is null once
 * dispatch ends, so it cannot be read later. Every existing call site invokes
 * a handler and simply ignores the payload.
 */
const emit = defineEmits<{ click: [event: Event] }>()

/** A chip that emits — as opposed to one that navigates, or one that is text. */
const clickable = computed(() => props.interactive && !props.href && !props.gone)
/** A chip the browser navigates for us. */
const linked = computed(() => !!props.href && !props.gone)

const ICON_BY_KIND: Record<RefChipKind, string> = {
  requirement: 'pi-hashtag',
  clause: 'pi-hashtag',
  schedule: 'pi-calendar',
  finding: 'pi-exclamation-circle',
  document: 'pi-file',
  task: 'pi-check-square',
  overflow: '',
}

/** Identifier kinds read as code; everything else is prose. */
const MONO_KINDS: readonly RefChipKind[] = ['requirement', 'clause', 'overflow']

const icon = computed(() => {
  // `gone` outranks `hideIcon`: the ban mark IS the reason the chip is dead,
  // and a surface's own marker never says that.
  if (props.gone) return 'pi-ban'
  if (props.hideIcon) return ''
  if (props.kind === 'document' && props.concept) return 'pi-search'
  return ICON_BY_KIND[props.kind]
})

/** A filename is longer and denser than a clause code, so it gets the bigger
 * size, the surface tint that makes it read as a THING, and room to truncate. */
const isDocument = computed(() => props.kind === 'document')

const chipClass = computed(() => [
  isDocument.value ? 'gap-1 text-xs max-w-full align-middle' : 'gap-0.5 text-[10px]',
  MONO_KINDS.includes(props.kind) ? 'font-mono' : '',
  isDocument.value && !props.concept ? 'bg-surface-100 dark:bg-surface-800' : '',
  isDocument.value && props.concept ? 'border-dashed' : '',
  props.gone ? 'opacity-60 cursor-not-allowed' : '',
  clickable.value || linked.value ? 'cursor-pointer hover:border-primary-400 hover:text-primary-600' : '',
])

/**
 * Propagation is stopped only when the chip actually HANDLES the gesture.
 *
 * A static chip must stay transparent to its row: on the plan the whole row is
 * a button that opens the action, and a chip swallowing that click would make
 * part of the row dead. A linked chip stops the bubble but not the default —
 * the ancestor must not move the page out from under the new tab, and the
 * browser still has to follow the link.
 */
function onActivate(e: Event): void {
  if (linked.value) {
    e.stopPropagation()
    return
  }
  if (!clickable.value) return
  e.stopPropagation()
  e.preventDefault()
  emit('click', e)
}
</script>

<template>
  <component
    :is="href && !gone ? 'a' : 'span'"
    class="inline-flex items-center rounded border border-surface-300 dark:border-surface-600 px-1 py-px text-color-secondary"
    :class="chipClass"
    :href="linked ? href : undefined"
    :target="linked ? '_blank' : undefined"
    :rel="linked ? 'noopener noreferrer' : undefined"
    :role="clickable || gone ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    :aria-disabled="gone ? 'true' : undefined"
    v-tooltip.top="title"
    @click="onActivate"
    @keydown.enter="onActivate"
    @keydown.space="onActivate"
  >
    <i v-if="icon" class="pi shrink-0" :class="[icon, isDocument ? 'text-[10px]' : 'text-[9px]']"
      aria-hidden="true" />
    <slot>{{ label }}</slot>
  </component>
</template>
